import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Trash2, ShoppingCart, AlertCircle, Clock, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  getCartItems,
  getCartSubtotal,
  removeFromCart,
  clearCart,
  CART_UPDATED_EVENT,
  type CartItem,
  addToCart,
  isInCart,
} from '@/lib/cartStore';
import { fireEvent } from '@/lib/analytics';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { createStripeCheckout, createStripeLtoCheckout } from '@/lib/checkoutApi';
import { useFavorites } from '@/hooks/useFavorites';

export default function Cart() {
  const navigate = useNavigate();
  const [items, setItems] = useState<CartItem[]>([]);
  const [unavailableItems, setUnavailableItems] = useState<Set<string>>(new Set());
  const [isValidating, setIsValidating] = useState(true);
  const { favorites, remove: removeFavorite } = useFavorites();
  const [favoriteDomains, setFavoriteDomains] = useState<
    { domain: string; bin_price: number; tags?: string[] | null; tld?: string | null }[]
  >([]);

  // Load cart items
  useEffect(() => {
    loadCart();
  }, []);

  // Listen for cart updates
  useEffect(() => {
    const handleCartUpdate = () => {
      loadCart();
    };

    window.addEventListener(CART_UPDATED_EVENT, handleCartUpdate);
    return () => {
      window.removeEventListener(CART_UPDATED_EVENT, handleCartUpdate);
    };
  }, []);

  // Track page view
  useEffect(() => {
    const cartItems = getCartItems();
    fireEvent('view_cart', {
      cart_count: cartItems.length,
      cart_value: getCartSubtotal(),
    });
  }, []);

  const loadCart = async () => {
    const cartItems = getCartItems();
    setItems(cartItems);

    // Validate availability
    if (cartItems.length > 0) {
      setIsValidating(true);
      try {
        const domains = cartItems.map((item) => item.domain);
        const { data } = await supabase
          .from('domains')
          .select('domain, owned, domain_is_live')
          .in('domain', domains);

        const unavailable = new Set<string>();
        cartItems.forEach((item) => {
          const domainData = data?.find(
            (d) => d.domain.toLowerCase() === item.domain.toLowerCase(),
          );
          if (!domainData || !domainData.owned || !domainData.domain_is_live) {
            unavailable.add(item.domain);
          }
        });

        setUnavailableItems(unavailable);
      } catch (error) {
        console.error('Error validating cart items:', error);
      } finally {
        setIsValidating(false);
      }
    } else {
      setIsValidating(false);
    }
  };

  const handleRemove = (domain: string) => {
    const removed = removeFromCart(domain);

    if (removed) {
      fireEvent('remove_from_cart', {
        domain_id: domain,
        slug: domain,
      });

      toast({
        title: 'Removed from cart',
        description: `${domain} has been removed from your cart.`,
      });
    }
  };

  // Load favorite domain data
  useEffect(() => {
    const loadFavorites = async () => {
      if (!favorites.length) {
        setFavoriteDomains([]);
        return;
      }
      try {
        const { data } = await supabase
          .from('domains')
          .select('domain, bin_price, tags, tld, owned, domain_is_live')
          .in('domain', favorites);

        const cleaned =
          data?.filter((d) => d.owned && d.domain_is_live).map((d) => ({
            domain: d.domain,
            bin_price: d.bin_price,
            tags: d.tags,
            tld: d.tld,
          })) || [];
        setFavoriteDomains(cleaned);
      } catch (error) {
        console.error('Error loading favorites data:', error);
        setFavoriteDomains([]);
      }
    };

    loadFavorites();
  }, [favorites]);

  const handleAddFavoriteToCart = (fav: { domain: string; bin_price: number; tld?: string | null }) => {
    if (isInCart(fav.domain)) {
      navigate('/cart');
      return;
    }

    const added = addToCart({
      domain: fav.domain,
      tld: fav.tld || fav.domain.split('.').pop() || '',
      binPrice: fav.bin_price,
    });

    if (added) {
      toast({
        title: 'Added to cart',
        description: `${fav.domain} has been added to your cart.`,
      });
    } else {
      toast({
        title: 'Already in cart',
        description: `${fav.domain} is already in your cart.`,
      });
    }
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all items from your cart?')) {
      const prevCount = clearCart();

      toast({
        title: 'Cart cleared',
        description: `Removed ${prevCount} items from your cart.`,
      });
    }
  };

  const handleBeginCheckout = async () => {
    const availableItems = items.filter((item) => !unavailableItems.has(item.domain));

    if (availableItems.length === 0) {
      toast({
        title: 'No available items',
        description: 'Please remove unavailable items before checking out.',
        variant: 'destructive',
      });
      return;
    }

    // Separate BIN and lease items
    const binItems = availableItems.filter(item => !item.lease);
    const leaseItems = availableItems.filter(item => item.lease);

    // Validate lease items have required fields
    const invalidLeaseItems = leaseItems.filter(
      item => !item.leaseTerm || !item.leaseMonthly
    );

    if (invalidLeaseItems.length > 0) {
      toast({
        title: 'Invalid lease items',
        description: 'Some lease items are missing required information. Please remove and re-add them from the lease detail page.',
        variant: 'destructive',
      });
      return;
    }

    // Handle mixed cart (BIN + Lease) - not supported, direct user to checkout separately
    if (binItems.length > 0 && leaseItems.length > 0) {
      toast({
        title: 'Mixed cart',
        description: 'Please checkout BIN purchases and leases separately. Lease items should be checked out directly from their lease detail pages.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Handle lease checkout
      if (leaseItems.length > 0) {
        // Each lease requires individual checkout (Stripe subscription per domain)
        if (leaseItems.length > 1) {
          toast({
            title: 'Multiple leases',
            description: 'Please checkout leases one at a time from their lease detail pages.',
            variant: 'destructive',
          });
          return;
        }

        const leaseItem = leaseItems[0];
        
        // Track checkout attempt
        fireEvent('begin_checkout', {
          cart_count: 1,
          cart_value: leaseItem.leaseMonthly || 0,
          lease: true,
          lease_term: leaseItem.leaseTerm,
        });

        const term = `${leaseItem.leaseTerm}m` as '12m' | '24m' | '36m';
        const checkoutUrl = await createStripeLtoCheckout(leaseItem.domain, term);
        
        // Redirect to Stripe checkout
        window.location.href = checkoutUrl;
        return;
      }

      // Handle BIN checkout (existing logic)
      // Track checkout attempt
      fireEvent('begin_checkout', {
        cart_count: binItems.length,
        cart_value: binItems.reduce((sum, item) => sum + item.binPrice, 0),
      });

      // Create Stripe checkout session with quoted total for price validation
      const checkoutUrl = await createStripeCheckout({
        items: binItems.map(item => ({ domain: item.domain })),
        quotedTotalCents: Math.round(
          binItems.reduce((sum, item) => sum + item.binPrice, 0) * 100
        ),
      });

      // Redirect to Stripe checkout
      window.location.href = checkoutUrl;
    } catch (error) {
      // Error logging - in production this should go to Sentry
      if (process.env.NODE_ENV === 'development') {
        console.error('Checkout error:', error);
      }
      toast({
        title: 'Checkout failed',
        description: error instanceof Error ? error.message : 'Unable to start checkout. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const availableItems = items.filter((item) => !unavailableItems.has(item.domain));
  const subtotal = availableItems.reduce((sum, item) => {
    // For lease items, show first month payment in subtotal
    if (item.lease && item.leaseMonthly) {
      return sum + item.leaseMonthly;
    }
    return sum + item.binPrice;
  }, 0);

  return (
    <>
      <Helmet>
        <title>Shopping Cart | Attain Domains</title>
        <meta
          name="description"
          content="Review your selected premium domains and proceed to checkout."
        />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="min-h-screen bg-background py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Favorites strip */}
          {favorites.length > 0 && favoriteDomains.length > 0 && (
            <Card className="mb-8 border border-slate-200 shadow-sm">
              <CardContent className="p-6 space-y-4">
                <div className="flex flex-col items-center text-center gap-3 stagger-fade-1">
                  <div className="flex items-center justify-center gap-2">
                    <h2 className="text-6xl font-bold text-foreground">Saved</h2>
                    <span className="w-20 h-20 flex items-center justify-center text-primary text-6xl font-bold leading-none">
                      +
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground"></p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  {favoriteDomains.map((fav) => (
                    <div
                      key={fav.domain}
                      className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <Link
                            to={`/domain/${fav.domain}`}
                            className="text-lg font-semibold text-foreground hover:text-primary transition-colors"
                          >
                            {fav.domain}
                          </Link>
                          <div className="text-sm text-muted-foreground">{formatPrice(fav.bin_price)}</div>
                          {fav.tags && fav.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {fav.tags.slice(0, 3).map((tag) => (
                                <span
                                  key={tag}
                                  className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => removeFavorite(fav.domain)}
                        >
                          Remove
                        </Button>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          className="w-full"
                          onClick={() => handleAddFavoriteToCart(fav)}
                          variant={isInCart(fav.domain) ? 'secondary' : 'default'}
                        >
                          {isInCart(fav.domain) ? 'In Cart' : 'Add to cart'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Empty State */}
          {items.length === 0 && (
            <Card className="text-center py-16">
              <CardContent>
                <ShoppingCart className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-foreground mb-2">Your cart is empty</h2>
                <p className="text-muted-foreground mb-6">
                  Browse our premium domains and add them to your cart to get started.
                </p>
                <Button onClick={() => navigate('/domains')}>
                  Browse Domains
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Cart Items */}
          {items.length > 0 && (
            <div className="space-y-6">
              {/* Items List + Checkout Summary in one card */}
              <Card>
                <CardContent className="p-6 space-y-6">
                  <div className="flex items-center justify-center gap-4 text-center stagger-fade-1">
                    <h1 className="text-6xl font-bold text-foreground">Cart</h1>
                    <ShoppingCart className="w-16 h-16 text-primary" />
                  </div>
                  <p className="text-sm text-muted-foreground text-center mt-1"></p>

                  <div className="space-y-4">
                    {items.map((item) => {
                      const isUnavailable = unavailableItems.has(item.domain);

                      return (
                        <div
                          key={item.id}
                          className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                            isUnavailable
                              ? 'bg-destructive/5 border-destructive/20'
                              : 'bg-card border-border hover:bg-accent/50'
                          }`}
                        >
                          <div className="flex-1">
                            <Link
                              to={`/domain/${item.domain}`}
                              className="text-lg font-bold text-foreground hover:text-primary transition-colors"
                            >
                              {item.domain}
                            </Link>

                            {/* Lease Badge & Info */}
                            {item.lease && item.leaseTerm && item.leaseMonthly && (
                              <div className="mt-2 inline-flex flex-col gap-1">
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded w-fit">
                                  <Clock className="w-3 h-3" />
                                  Lease ({item.leaseTerm} months)
                                </span>
                                <p className="text-sm text-muted-foreground">
                                  ${item.leaseMonthly.toLocaleString()}/month • Total: $
                                  {(item.leaseMonthly * item.leaseTerm).toLocaleString()}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Option to buy at ${item.binPrice.toLocaleString()} anytime
                                </p>
                              </div>
                            )}

                            {isUnavailable && (
                              <Alert className="mt-2 py-2 border-destructive/50">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription className="text-sm">
                                  This domain is no longer available. Please remove it from your
                                  cart.
                                </AlertDescription>
                              </Alert>
                            )}
                          </div>

                          <div className="flex items-center gap-4 ml-4">
                            <span
                              className={`text-lg font-semibold ${isUnavailable ? 'text-muted-foreground line-through' : 'text-foreground'}`}
                            >
                              {formatPrice(item.binPrice)}
                            </span>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemove(item.domain)}
                              className="text-muted-foreground hover:text-destructive"
                              aria-label={`Remove ${item.domain} from cart`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="space-y-4 border border-border rounded-lg bg-white shadow-sm p-6">
                    <div className="flex items-center text-lg">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-semibold text-foreground ml-auto pr-12 text-right">
                        {formatPrice(subtotal)}
                      </span>
                    </div>

                    {unavailableItems.size > 0 && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          {unavailableItems.size}{' '}
                          {unavailableItems.size === 1 ? 'item is' : 'items are'} no longer available.
                          Please remove {unavailableItems.size === 1 ? 'it' : 'them'} before checking out.
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="pt-4 border-t border-border">
                      <p className="text-sm text-muted-foreground mb-4"></p>

                        <div className="flex justify-center">
                        <Button
                          size="lg"
                            className="group w-full sm:w-1/2 text-white transition-all duration-200 transform hover:scale-115 hover:shadow-[0_0_28px_rgba(0,0,255,0.55)] text-3xl py-6 min-h-[68px]"
                          style={{
                            background: 'linear-gradient(135deg, #0000FF 0%, #000000 100%)',
                          }}
                          onClick={handleBeginCheckout}
                          disabled={availableItems.length === 0}
                            onMouseEnter={(e) => {
                              const target = e.currentTarget;
                              target.dataset.originalBg = target.style.background;
                              target.style.background = 'linear-gradient(135deg, #000000 0%, #0000FF 100%)';
                            }}
                            onMouseLeave={(e) => {
                              const target = e.currentTarget;
                              target.style.background =
                                target.dataset.originalBg || 'linear-gradient(135deg, #0000FF 0%, #000000 100%)';
                            }}
                        >
                            Checkout
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
